import React, { forwardRef, useImperativeHandle, useState } from "react";
import { useTranslation } from "react-i18next";
import { IconBrandWhatsapp } from "@tabler/icons-react";
import MDialog from "@/components/ui/atoms/MDialog";
import { MOtp } from "@/components/ui/molecules/MOtp";
import { getMemberInfo, useProfileUpdate } from "@/hooks/useMembers";
import { otpService } from "@/services/otp.service";
import { useToast } from "@/components/ui/atoms/MToastProvider";

const WHATSAPP_NUMBER = "919870597100";
const DIAL_CODE = "+91";
const RESEND_SECONDS = 30;

export interface WhatsAppContactHandle {
  /**
   * Open Maatra on WhatsApp. If the member has no mobile number, runs the
   * add-mobile → OTP verification flow first, then opens WhatsApp.
   */
  send: () => void;
}

interface WhatsAppContactProps {
  /** Called whenever WhatsApp is opened (directly, or after mobile verification). */
  onOpened?: () => void;
}

const openWhatsApp = () => {
  // wa.me opens WhatsApp directly on both iOS and Android without showing
  // a system "send message" picker. The whatsapp:// scheme triggers that picker.
  globalThis.open?.(`https://wa.me/${WHATSAPP_NUMBER}`, "_blank");
};

/**
 * Renders the dialogs needed to open WhatsApp, mirroring the DocumentSend flow:
 * if the member has a mobile number it opens WhatsApp directly, otherwise it
 * prompts to add and verify a mobile number first. Trigger via the `send()`
 * method exposed on the ref.
 */
const WhatsAppContact = forwardRef<WhatsAppContactHandle, WhatsAppContactProps>(
  ({ onOpened }, ref) => {
    const { t } = useTranslation();
    const memberInfo = getMemberInfo(true);
    const { showToast } = useToast();
    const { handleContactUpdate } = useProfileUpdate();

    const [showMissingMobileDialog, setShowMissingMobileDialog] =
      useState(false);
    const [showAddMobileDialog, setShowAddMobileDialog] = useState(false);
    const [showMobileOtpDrawer, setShowMobileOtpDrawer] = useState(false);
    const [showSuccessDialog, setShowSuccessDialog] = useState(false);
    const [mobileInput, setMobileInput] = useState("");

    const extractErrorMessage = (error: unknown, fallback: string): string => {
      const errObj = error as Record<string, unknown>;
      const message = (errObj?.response as Record<string, unknown>)
        ?.data as Record<string, unknown>;
      if (typeof message === "string") return message;
      if (typeof message?.message === "string")
        return message.message as string;
      if (typeof errObj?.message === "string") return errObj.message as string;
      return fallback;
    };

    const triggerWhatsApp = () => {
      openWhatsApp();
      onOpened?.();
    };

    const handleSend = () => {
      if (!memberInfo.mobile || memberInfo.mobile.trim() === "") {
        setShowMissingMobileDialog(true);
        return;
      }
      triggerWhatsApp();
    };

    useImperativeHandle(ref, () => ({ send: handleSend }));

    const isValidMobile = (mobile: string) =>
      mobile.length === 10 && /^\d+$/.test(mobile);

    const handleVerifyMobile = async () => {
      try {
        await otpService.sendOtp({
          send_to: mobileInput,
          dialCode: DIAL_CODE,
          mode: "sms",
          type: "member_update",
        });
        setShowAddMobileDialog(false);
        setShowMobileOtpDrawer(true);
      } catch (error: unknown) {
        showToast({
          message: extractErrorMessage(
            error,
            t("whatsappContact.sendCodeFailed")
          ),
          variant: "error",
        });
      }
    };

    const handleMobileResend = async () => {
      try {
        await otpService.sendOtp({
          send_to: mobileInput,
          dialCode: DIAL_CODE,
          mode: "sms",
          type: "member_update",
        });
        showToast({
          message: t("whatsappContact.codeSent"),
          variant: "success",
        });
      } catch (error: unknown) {
        showToast({
          message: extractErrorMessage(
            error,
            t("whatsappContact.resendFailed")
          ),
          variant: "error",
        });
      }
    };

    const handleMobileOtpVerify = async (
      otp: string
    ): Promise<true | string> => {
      try {
        await otpService.verifyOtp({
          send_to: mobileInput,
          dialCode: DIAL_CODE,
          otp,
          mode: "sms",
          type: "member_update",
        });

        const result = await handleContactUpdate({
          memberId: memberInfo.id,
          updateData: { dialCode: DIAL_CODE, mobile: mobileInput },
          isChanged: true,
        });

        if (!result.success) {
          throw new Error("Failed to update contact information");
        }

        setShowMobileOtpDrawer(false);
        setShowSuccessDialog(true);
        setMobileInput("");
        setShowAddMobileDialog(false);
        return true;
      } catch (error) {
        throw new Error(t("whatsappContact.invalidCode"));
      }
    };

    const handleMobileEdit = () => {
      setShowMobileOtpDrawer(false);
      setShowAddMobileDialog(true);
    };

    const handleSuccessClose = () => {
      setShowSuccessDialog(false);
      triggerWhatsApp();
    };

    return (
      <>
        {/* Missing Mobile Dialog */}
        <MDialog
          isOpen={showMissingMobileDialog}
          onClose={() => setShowMissingMobileDialog(false)}
          title={t("whatsappContact.missingMobileTitle")}
          description={t("whatsappContact.missingMobileDescription")}
          showIconVariant={true}
          icon={<IconBrandWhatsapp size={32} color="#8764BB" />}
          primaryCtaText={t("whatsappContact.addMobile")}
          secondaryCtaText={t("common.cancel")}
          onPrimaryCta={() => {
            setShowMissingMobileDialog(false);
            setShowAddMobileDialog(true);
          }}
          onSecondaryCta={() => setShowMissingMobileDialog(false)}
        />

        {/* Add Mobile Input Dialog */}
        <MDialog
          isOpen={showAddMobileDialog}
          onClose={() => setShowAddMobileDialog(false)}
          showIconVariant={true}
          icon={<IconBrandWhatsapp size={32} color="#8764BB" />}
          showInputVariant={true}
          inputType="tel"
          inputPlaceholder={t("whatsappContact.mobilePlaceholder")}
          inputLabel={t("whatsappContact.mobileLabel")}
          showLabel={true}
          showDialCode={true}
          dialCode={DIAL_CODE}
          inputValue={mobileInput}
          onInputChange={setMobileInput}
          primaryCtaText={t("whatsappContact.verify")}
          onPrimaryCta={handleVerifyMobile}
          isPrimaryCtaDisabled={!isValidMobile(mobileInput)}
        />

        {/* Mobile OTP Drawer */}
        <MOtp
          open={showMobileOtpDrawer}
          onClose={() => setShowMobileOtpDrawer(false)}
          onDone={handleMobileOtpVerify}
          onResend={handleMobileResend}
          onEdit={handleMobileEdit}
          title={t("whatsappContact.verifyTitle")}
          sendTo={`${DIAL_CODE} ${mobileInput}`}
          resendTimer={RESEND_SECONDS}
        />

        {/* Success Dialog */}
        <MDialog
          isOpen={showSuccessDialog}
          onClose={handleSuccessClose}
          showIconVariant={true}
          icon={<IconBrandWhatsapp size={32} color="#8764BB" />}
          title={t("whatsappContact.successTitle")}
          description={t("whatsappContact.successDescription")}
          primaryCtaText={t("whatsappContact.openWhatsApp")}
          onPrimaryCta={handleSuccessClose}
        />
      </>
    );
  }
);

WhatsAppContact.displayName = "WhatsAppContact";

export default WhatsAppContact;
